import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { Game, SportType } from '@/types';
import { formatDate, formatUtcToLocalDate, formatUtcToLocalTime } from '@/lib/utils';
import { BookmarkCheck, BookmarkPlus, CalendarDays, Clock, MapPin } from 'lucide-react';
import CompetitionIcon from './CompetitionIcons';
import TeamLogo from './TeamLogo';
import { useLocalAppState } from '@/contexts/LocalAppStateContext';

interface UpcomingGameCardProps {
  game: Game;
}

function getKickoffLabel(utcDatetime: string): string {
  const diffMs = new Date(utcDatetime).getTime() - Date.now();

  if (diffMs <= 0) {
    return 'Starting soon';
  }

  const diffHours = Math.round(diffMs / (1000 * 60 * 60));
  if (diffHours < 24) {
    return `Starts in ${diffHours}h`;
  }

  const diffDays = Math.round(diffMs / (1000 * 60 * 60 * 24));
  return diffDays === 1 ? 'Starts tomorrow' : `Starts in ${diffDays} days`;
}

function formatTeamName(game: Game, team: Game['homeTeam']): string {
  if (game.sportType === 'soccer') {
    return team.name;
  }

  return `${team.city} ${team.name}`.trim();
}

function getSecondaryTeamLabel(game: Game, team: Game['homeTeam']): string | null {
  if (game.sportType !== 'soccer') {
    return null;
  }

  if (!team.city || team.city === 'World' || team.city === team.name) {
    return null;
  }

  return team.city;
}

function formatStageLabel(stage: string | undefined, fallback: string): string {
  if (!stage) {
    const normalizedFallback = fallback.trim();

    if (!normalizedFallback) {
      return 'Scheduled';
    }

    // Some providers put source timezone strings like "7:05 PM ET" in status.detail.
    // Upcoming pages should show the local client time in the time slot, not here.
    if (/\d/.test(normalizedFallback) || /\b(?:am|pm|et|ct|mt|pt)\b/i.test(normalizedFallback)) {
      return 'Scheduled';
    }

    return normalizedFallback;
  }

  return stage
    .split('_')
    .map((segment) => segment.charAt(0).toUpperCase() + segment.slice(1).toLowerCase())
    .join(' ');
}

function getAccentClasses(sport: SportType, startsSoon: boolean) {
  if (sport === 'soccer') {
    return startsSoon
      ? 'bg-[linear-gradient(90deg,rgba(52,211,153,0.95),rgba(99,102,241,0.78))]'
      : 'bg-[linear-gradient(90deg,rgba(34,197,94,0.88),rgba(148,163,184,0.24))]';
  }

  if (sport === 'nba') {
    return startsSoon
      ? 'bg-[linear-gradient(90deg,rgba(251,146,60,0.96),rgba(99,102,241,0.78))]'
      : 'bg-[linear-gradient(90deg,rgba(234,88,12,0.82),rgba(148,163,184,0.24))]';
  }

  if (sport === 'nfl') {
    return startsSoon
      ? 'bg-[linear-gradient(90deg,rgba(245,158,11,0.96),rgba(99,102,241,0.78))]'
      : 'bg-[linear-gradient(90deg,rgba(180,83,9,0.84),rgba(148,163,184,0.24))]';
  }

  if (sport === 'mlb') {
    return startsSoon
      ? 'bg-[linear-gradient(90deg,rgba(96,165,250,0.96),rgba(99,102,241,0.78))]'
      : 'bg-[linear-gradient(90deg,rgba(37,99,235,0.84),rgba(148,163,184,0.24))]';
  }

  return startsSoon
    ? 'bg-[linear-gradient(90deg,rgba(34,211,238,0.96),rgba(99,102,241,0.78))]'
    : 'bg-[linear-gradient(90deg,rgba(8,145,178,0.84),rgba(148,163,184,0.24))]';
}

const UpcomingGameCard = ({ game }: UpcomingGameCardProps) => {
  const { state, saveGame } = useLocalAppState();
  const isSaved = state.replayPlan.some((item) => item.id === game.id);
  const localDate = formatUtcToLocalDate(game.utcDatetime) ?? formatDate(game.date);
  const localTime = formatUtcToLocalTime(game.utcDatetime) ?? 'TBD';
  const kickoffLabel = getKickoffLabel(game.utcDatetime);
  const startsSoon = new Date(game.utcDatetime).getTime() - Date.now() <= 1000 * 60 * 60 * 48;
  const accentClasses = getAccentClasses(game.sportType, startsSoon);
  const showArena = Boolean(game.arena && game.arena !== 'Unknown');

  return (
    <Card className="overflow-hidden border-white/10 bg-slate-900/78 text-white shadow-[0_18px_48px_rgba(2,6,23,0.24)]">
      <div className={`h-1 w-full ${accentClasses}`} />

      <CardHeader className="space-y-4 border-b border-white/8 bg-[linear-gradient(180deg,rgba(15,23,42,0.96),rgba(15,23,42,0.72))] px-5 py-4">
        <div className="flex items-start justify-between gap-3">
          <div>
            <p className="flex items-center gap-2 text-sm font-semibold text-slate-100">
              <CalendarDays className="h-4 w-4 text-indigo-300" />
              {localDate}
            </p>
            <p className="mt-1 text-xs uppercase tracking-[0.18em] text-slate-400">
              {kickoffLabel}
            </p>
          </div>

          {game.competition && (
            <Badge
              variant="outline"
              className="max-w-[13rem] gap-1 border-emerald-400/20 bg-emerald-500/10 px-2.5 py-1 text-emerald-100"
            >
              <CompetitionIcon competition={game.competition} className="h-4 w-4" />
              <span className="truncate">{game.competition}</span>
            </Badge>
          )}
        </div>

        <div className="flex items-center justify-between gap-3">
          <Badge
            variant="outline"
            className="border-white/10 bg-white/5 px-2.5 py-1 text-xs font-medium text-slate-200"
          >
            {formatStageLabel(game.stage, game.status.detail)}
          </Badge>

          <div className="flex items-center gap-2 text-sm text-slate-300">
            <Clock className="h-4 w-4 text-indigo-300" />
            <span>{localTime}</span>
          </div>
        </div>
      </CardHeader>

      <CardContent className="px-5 py-5">
        <div className="space-y-4">
          {[game.homeTeam, game.awayTeam].map((team) => {
            const secondaryLabel = getSecondaryTeamLabel(game, team);

            return (
              <div key={team.id} className="flex items-center gap-3 rounded-2xl border border-white/8 bg-white/[0.03] px-3 py-3">
              <TeamLogo team={team} sportType={game.sportType} size="lg" />
              <div className="min-w-0">
                <p className="truncate text-lg font-semibold text-white">{formatTeamName(game, team)}</p>
                {secondaryLabel && <p className="truncate text-sm text-slate-400">{secondaryLabel}</p>}
              </div>
            </div>
            );
          })}
        </div>

        {showArena && (
          <div className="mt-5 flex items-start gap-2 rounded-2xl border border-white/8 bg-slate-950/60 px-3 py-3 text-sm text-slate-300">
            <MapPin className="mt-0.5 h-4 w-4 shrink-0 text-indigo-300" />
            <span>{game.arena}</span>
          </div>
        )}
        <button type="button" disabled={isSaved} onClick={() => saveGame(game)} className="mt-5 inline-flex min-h-11 w-full items-center justify-center gap-2 rounded-xl bg-indigo-500 px-4 text-sm font-semibold text-white hover:bg-indigo-400 focus-visible:outline focus-visible:outline-2 focus-visible:outline-indigo-200 disabled:bg-emerald-500/15 disabled:text-emerald-200">
          {isSaved ? <BookmarkCheck className="h-4 w-4" /> : <BookmarkPlus className="h-4 w-4" />}
          {isSaved ? 'Saved to My Replay Plan' : 'Save to My Replay Plan'}
        </button>
      </CardContent>
    </Card>
  );
};

export default UpcomingGameCard;
